import { FC, useEffect, useRef, useState, useMemo } from 'react';
import { RawDraftContentState, EditorState } from 'draft-js';
import { Typography, Box } from '@mui/material';
import { Draft } from '../index';
import { DraftActionType } from '../DraftActions';
import { getCount } from '../CharCounter/utils';
import { Plugin } from '@prismamedia/one-components';

interface DraftWithBlockCounterProps {
  actions: DraftActionType[];
  className?: string;
  handlePastedText?: (
    text: string,
    html: string,
    editorState: EditorState,
  ) => boolean;
  onChange: (value: RawDraftContentState) => void;
  onSelection?: (editorState: EditorState) => void;
  placeholder?: string | JSX.Element;
  plugins?: Plugin[];
  rawDraft: RawDraftContentState | null;
  readOnly?: boolean;
  showBlockCounters?: boolean;
}

export const DraftWithBlockCounter: FC<DraftWithBlockCounterProps> = ({
  showBlockCounters = true,
  ...props
}) => {
  const draftContainerRef = useRef<HTMLDivElement>(null);
  const [blockElements, setBlockElements] = useState<Element[]>([]);

  // Calculate character counts directly from rawDraft - fast and reliable!
  const blockCounts = useMemo(() => {
    if (!props.rawDraft?.blocks) return [];
    return props.rawDraft.blocks.map((block) => {
      const { charCount } = getCount(block.text);
      return charCount;
    });
  }, [props.rawDraft]);

  const updateBlockElements = () => {
    if (draftContainerRef.current) {
      const draftEditor = draftContainerRef.current.querySelector(
        '.public-DraftEditor-content',
      );

      if (draftEditor) {
        const blocks = Array.from(
          draftEditor.querySelectorAll('[data-block="true"]'),
        );

        setBlockElements(blocks);
      }
    }
  };

  useEffect(() => {
    updateBlockElements();
  }, [props.rawDraft]);

  useEffect(() => {
    updateBlockElements();
  }, []);

  return (
    <Box ref={draftContainerRef} sx={{ position: 'relative' }}>
      <Draft {...props} />

      {/* Character count overlays */}
      {showBlockCounters && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: -70,
            width: '60px',
            pointerEvents: 'none',
          }}
        >
          {blockElements.map((blockElement, index) => {
            const charCount = blockCounts[index];

            // Skip if no count, empty, or index mismatch
            if (!charCount || index >= blockCounts.length) return null;

            const containerRect = draftContainerRef.current?.getBoundingClientRect();
            const blockRect = blockElement.getBoundingClientRect();

            return (
              <Box
                key={`${index}-${charCount}`}
                sx={{
                  position: 'absolute',
                  right: 0,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                  paddingTop: 1,
                  width: '60px',
                  top: containerRect ? blockRect.top - containerRect.top : 0,
                  height: blockRect.height,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'grey.500',
                    fontSize: '12px !important',
                    fontWeight: '400 !important',
                    lineHeight: '1.2 !important',
                    userSelect: 'none',
                    textAlign: 'right',
                  }}
                >
                  {charCount}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
